import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Shield,
  Car,
  Calendar,
  FileText,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertTriangle,
  MapPin,
  User,
  Building,
  Wrench,
  Upload,
  Info,
  Paperclip
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import PortalLayout from '../components/PortalLayout';
import DocumentUploader from '../components/DocumentUploader';
import { getUserFriendlyError } from '../utils/errorMessages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency formatting
const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  if (num % 1 === 0) {
    return `$${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  }
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Date formatting
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

export default function FileClaimPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken, logout } = useAuth();
  
  // State
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [userName, setUserName] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [newClaimNumber, setNewClaimNumber] = useState('');
  
  // Eligibility state
  const [eligibility, setEligibility] = useState(null);
  const [eligibilityBlocked, setEligibilityBlocked] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    policy_number: searchParams.get('policy') || '',
    // Accident details
    accident_date: '',
    accident_description: '',
    accident_location: '',
    police_report_number: '',
    police_report_available: false,
    // At-fault party
    at_fault_name: '',
    at_fault_insurance: '',
    at_fault_policy_number: '',
    at_fault_claim_number: '',
    // Repair info
    repair_status: 'not_started',
    repair_shop_name: '',
    repair_shop_address: '',
    estimated_repair_cost: '',
    actual_repair_cost: ''
  });
  
  // Document uploads (for Step 5)
  const [uploadedDocuments, setUploadedDocuments] = useState([]);

  useEffect(() => {
    fetchUserPolicies();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const fetchUserPolicies = async () => {
    const token = getToken();
    if (!token) {
      navigate('/portal/login');
      return;
    }

    try {
      setLoading(true);
      
      const response = await fetch(`${API_URL}/api/portal/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        await logout();
        navigate('/portal/login');
        return;
      }

      // Read as text first, then parse - avoids body read issues
      const responseText = await response.text();
      const data = responseText ? JSON.parse(responseText) : {};
      
      if (data.policyholder) {
        setUserName(data.policyholder.first_name || 'Policyholder');
      }

      // Get all policies
      const userPolicies = data.policies || [data.primary_policy].filter(Boolean);
      setPolicies(userPolicies);

      // Pre-select policy if provided in URL
      const policyFromUrl = searchParams.get('policy');
      if (policyFromUrl) {
        const matchedPolicy = userPolicies.find(p => p.policy_number === policyFromUrl);
        if (matchedPolicy) {
          // Call handlePolicySelect to trigger eligibility check
          await handlePolicySelectInternal(policyFromUrl, token);
        }
      } else if (userPolicies.length === 1) {
        // Auto-select if only one policy - also check eligibility
        await handlePolicySelectInternal(userPolicies[0].policy_number, token);
      }

    } catch (err) {
      setError(getUserFriendlyError(err.message));
    } finally {
      setLoading(false);
    }
  };

  // Internal function to check policy eligibility
  const handlePolicySelectInternal = async (policyNumber, authToken) => {
    const token = authToken || getToken();
    setFormData(prev => ({ ...prev, policy_number: policyNumber }));
    setEligibility(null);
    setEligibilityBlocked(false);
    
    try {
      const response = await fetch(`${API_URL}/api/claims/policy/${policyNumber}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Read as text first, then parse - avoids body read issues
        const responseText = await response.text();
        const data = responseText ? JSON.parse(responseText) : {};
        setSelectedPolicy(data.policy);
        
        // Check eligibility from the response
        const elig = data.eligibility;
        setEligibility(elig);
        
        if (!data.is_active) {
          setError('This policy is not currently active. Claims can only be filed against active policies.');
          setEligibilityBlocked(true);
        } else if (elig && !elig.can_file) {
          // Blocked due to eligibility rules
          setEligibilityBlocked(true);
          setError(null); // We'll show a custom UI instead of error
        } else {
          setError(null);
          setEligibilityBlocked(false);
        }
      }
    } catch (err) {
      console.error('Failed to fetch policy details:', err);
      setError(getUserFriendlyError(err.message));
    }
  };

  const handlePolicySelect = async (policyNumber) => {
    await handlePolicySelectInternal(policyNumber);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        // Must have policy selected AND pass eligibility check
        return !!formData.policy_number && !eligibilityBlocked;
      case 2:
        return formData.accident_date && formData.accident_description;
      case 3:
        return true; // At-fault info is optional
      case 4:
        return true; // Repair info is optional
      case 5:
        return true; // Documents are optional
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    const token = getToken();
    
    // Debug: Log token status
    console.log('[FileClaimPage] Submitting claim, token exists:', !!token);
    console.log('[FileClaimPage] API URL:', API_URL);
    
    if (!token) {
      setError('Your session has expired. Please log in again.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);

      console.log('[FileClaimPage] Sending claim request...');
      
      let response;
      try {
        response = await fetch(`${API_URL}/api/claims`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...formData,
            estimated_repair_cost: parseFloat(formData.estimated_repair_cost) || 0,
            actual_repair_cost: parseFloat(formData.actual_repair_cost) || 0
          })
        });
      } catch (networkError) {
        console.error('[FileClaimPage] Network error:', networkError);
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      }

      console.log('[FileClaimPage] Response status:', response.status);

      // Read response body as text first, then parse - avoids clone issues
      let responseText = '';
      let responseData;
      try {
        responseText = await response.text();
        console.log('[FileClaimPage] Response text:', responseText);
        
        // Try to parse as JSON
        if (responseText) {
          responseData = JSON.parse(responseText);
        } else {
          responseData = {};
        }
        console.log('[FileClaimPage] Response data:', responseData);
      } catch (parseError) {
        console.log('[FileClaimPage] JSON parse failed:', parseError);
        responseData = { detail: responseText || 'Unknown error occurred' };
      }

      // Check if response is ok
      if (!response.ok) {
        let errorMessage = 'Failed to submit claim';
        
        if (response.status === 401 || response.status === 403) {
          errorMessage = 'Your session has expired. Please log in again.';
        } else if (responseData.detail) {
          errorMessage = responseData.detail;
        } else {
          errorMessage = `Server error (${response.status}). Please try again.`;
        }
        
        console.log('[FileClaimPage] Error message:', errorMessage);
        throw new Error(errorMessage);
      }

      console.log('[FileClaimPage] Success:', responseData);
      const claimNumber = responseData.claim?.claim_number;
      setNewClaimNumber(claimNumber);
      
      // Upload documents if any were queued
      if (uploadedDocuments.length > 0 && claimNumber) {
        console.log('[FileClaimPage] Uploading', uploadedDocuments.length, 'documents...');
        
        for (const doc of uploadedDocuments) {
          try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', doc.file);
            formDataUpload.append('document_type', doc.document_type || 'other');
            
            const uploadResponse = await fetch(`${API_URL}/api/documents/upload/${claimNumber}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              },
              body: formDataUpload
            });
            
            if (uploadResponse.ok) {
              console.log('[FileClaimPage] Uploaded:', doc.filename);
            } else {
              console.error('[FileClaimPage] Failed to upload:', doc.filename);
            }
          } catch (uploadErr) {
            console.error('[FileClaimPage] Document upload error:', uploadErr);
          }
        }
      }
      
      setSubmitSuccess(true);
      setStep(7); // Success step

    } catch (err) {
      console.error('[FileClaimPage] Claim submission error:', err);
      setError(getUserFriendlyError(err.message || 'Failed to submit claim. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8EEFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#3B5BDB] mx-auto" />
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Steps configuration
  const steps = [
    { num: 1, label: 'Select Policy', icon: Shield },
    { num: 2, label: 'Accident Details', icon: AlertCircle },
    { num: 3, label: 'At-Fault Party', icon: User },
    { num: 4, label: 'Repair Info', icon: Wrench },
    { num: 5, label: 'Documents', icon: Paperclip },
    { num: 6, label: 'Review', icon: FileText }
  ];

  return (
    <PortalLayout pageTitle="File a Claim" userName={userName}>
      <div className="bg-[#E8EEFF] min-h-full py-8 md:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          
          {/* Header */}
          <div className="mb-8">
            <button 
              onClick={() => navigate('/claims')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Claims
            </button>
            <h1 className="text-3xl font-bold text-slate-900">File a New Claim</h1>
            <p className="text-slate-600 mt-2">
              Submit your diminished value claim in a few easy steps
            </p>
          </div>

          {/* Progress Steps */}
          {step <= 6 && (
            <div className="mb-8">
              <div className="flex items-center justify-between">
                {steps.map((s, idx) => {
                  const StepIcon = s.icon;
                  const isActive = step === s.num;
                  const isComplete = step > s.num;
                  
                  return (
                    <React.Fragment key={s.num}>
                      <div className="flex flex-col items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                          isComplete 
                            ? 'bg-green-500 text-white' 
                            : isActive 
                              ? 'bg-[#3B5BDB] text-white' 
                              : 'bg-slate-200 text-slate-500'
                        }`}>
                          {isComplete ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <StepIcon className="h-5 w-5" />
                          )}
                        </div>
                        <span className={`text-xs mt-2 hidden md:block ${isActive ? 'text-[#3B5BDB] font-medium' : 'text-slate-500'}`}>
                          {s.label}
                        </span>
                      </div>
                      {idx < steps.length - 1 && (
                        <div className={`flex-1 h-1 mx-2 rounded ${step > s.num ? 'bg-green-500' : 'bg-slate-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <Card className="mb-6 border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-red-700 font-medium">{error}</p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {step === 6 && (
                        <button
                          onClick={() => {
                            setError(null);
                            handleSubmit();
                          }}
                          className="text-sm font-medium text-red-600 hover:text-red-800 underline"
                        >
                          Try Again
                        </button>
                      )}
                      {(error.toLowerCase().includes('session') || error.toLowerCase().includes('expired') || error.toLowerCase().includes('authorization')) && (
                        <button
                          onClick={() => {
                            logout();
                            navigate('/signin');
                          }}
                          className="text-sm font-medium text-[#3B5BDB] hover:text-[#2F4BC7] underline"
                        >
                          Log in again
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step Content */}
          <Card className="shadow-xl border-0 rounded-2xl overflow-hidden bg-white">
            <CardContent className="p-4 sm:p-6 md:p-8 overflow-x-hidden">
              
              {/* Step 1: Select Policy */}
              {step === 1 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#E8EEFF] rounded-xl flex items-center justify-center">
                      <Shield className="h-5 w-5 text-[#3B5BDB]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Select Your Policy</h2>
                      <p className="text-slate-600 text-sm">Choose the policy you want to file a claim against</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {policies.map((policy) => {
                      const isSelected = formData.policy_number === policy.policy_number;
                      const isActive = ['active', 'issued', 'policy_issued', 'policy_issued_complete'].includes(policy.status?.toLowerCase());
                      
                      return (
                        <button
                          key={policy.policy_number}
                          onClick={() => isActive && handlePolicySelect(policy.policy_number)}
                          disabled={!isActive}
                          className={`w-full text-left p-5 rounded-xl border-2 transition-all ${
                            isSelected 
                              ? 'border-[#3B5BDB] bg-[#E8EEFF]' 
                              : isActive
                                ? 'border-slate-200 hover:border-slate-300 bg-white'
                                : 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-60'
                          }`}
                          data-testid={`policy-select-${policy.policy_number}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-mono font-bold text-slate-900">{policy.policy_number}</p>
                              <p className="text-slate-600 mt-1">
                                {policy.vehicle?.year} {policy.vehicle?.make} {policy.vehicle?.model}{policy.vehicle?.trim ? ` ${policy.vehicle.trim}` : ''}
                              </p>
                              <p className="text-sm text-slate-500 mt-1">
                                Coverage: {formatCurrency(policy.coverage_amount)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {!isActive && (
                                <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                                  {policy.status}
                                </span>
                              )}
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                isSelected ? 'border-[#3B5BDB] bg-[#3B5BDB]' : 'border-slate-300'
                              }`}>
                                {isSelected && <CheckCircle className="h-3 w-3 text-white" />}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {policies.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-slate-600">You don't have any active policies to file a claim against.</p>
                    </div>
                  )}

                  {/* Eligibility Block - Active Claim Exists */}
                  {eligibilityBlocked && eligibility?.reason_code === 'active_claim_exists' && (
                    <div className="mt-6 p-6 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="h-6 w-6 text-amber-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-amber-900 text-lg mb-2">Active Claim In Progress</h3>
                          <p className="text-amber-800 mb-4">
                            You already have an active claim for this vehicle. You can track or manage your existing claim below.
                          </p>
                          
                          {eligibility.active_claim && (
                            <div className="bg-white rounded-lg p-4 mb-4 border border-amber-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-mono font-semibold text-slate-900">
                                  {eligibility.active_claim.claim_number}
                                </span>
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                  {eligibility.active_claim.status_label}
                                </span>
                              </div>
                              <p className="text-sm text-slate-600">
                                Submitted: {formatDate(eligibility.active_claim.submitted_at)}
                              </p>
                            </div>
                          )}
                          
                          <div className="flex flex-wrap gap-3">
                            <Button
                              onClick={() => navigate('/claims')}
                              className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl"
                              data-testid="track-claim-btn"
                            >
                              Track Claim
                            </Button>
                            {eligibility.active_claim?.claim_number && (
                              <Button
                                variant="outline"
                                onClick={() => navigate(`/claims/${eligibility.active_claim.claim_number}`)}
                                className="rounded-xl"
                                data-testid="view-claim-btn"
                              >
                                View Claim Details
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Eligibility Block - Reassessment Required */}
                  {eligibilityBlocked && eligibility?.reason_code === 'reassessment_required' && (
                    <div className="mt-6 p-6 bg-purple-50 border border-purple-200 rounded-xl">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Car className="h-6 w-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-purple-900 text-lg mb-2">Vehicle Reassessment Required</h3>
                          <p className="text-purple-800 mb-4">
                            This vehicle has an existing claim history. Before filing a new claim, we need to reassess its current value.
                          </p>
                          
                          {eligibility.claim_history && eligibility.claim_history.settled_claims > 0 && (
                            <div className="bg-white rounded-lg p-4 mb-4 border border-purple-200">
                              <p className="text-sm text-slate-600">
                                <span className="font-medium">Previous Claims:</span> {eligibility.claim_history.settled_claims} settled
                              </p>
                              {eligibility.claim_history.last_settled_date && (
                                <p className="text-sm text-slate-600">
                                  <span className="font-medium">Last Settlement:</span> {formatDate(eligibility.claim_history.last_settled_date)}
                                </p>
                              )}
                            </div>
                          )}
                          
                          <Button
                            onClick={() => navigate(`/vehicle/reassessment?policy=${formData.policy_number}`)}
                            className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
                            data-testid="reassess-vehicle-btn"
                          >
                            Reassess Vehicle Value
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Accident Details */}
              {step === 2 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#E8EEFF] rounded-xl flex items-center justify-center">
                      <AlertCircle className="h-5 w-5 text-[#3B5BDB]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Accident Details</h2>
                      <p className="text-slate-600 text-sm">Tell us about the accident</p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <Label htmlFor="accident_date" className="text-slate-700 font-medium">
                        Date of Accident <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative mt-1">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 z-10" />
                        <Input
                          id="accident_date"
                          name="accident_date"
                          type="date"
                          value={formData.accident_date}
                          onChange={handleInputChange}
                          max={new Date().toISOString().split('T')[0]}
                          className="pl-10 h-12 rounded-xl w-full max-w-full box-border"
                          style={{ WebkitAppearance: 'none' }}
                          required
                          data-testid="accident-date-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="accident_description" className="text-slate-700 font-medium">
                        Description of Accident <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="accident_description"
                        name="accident_description"
                        value={formData.accident_description}
                        onChange={handleInputChange}
                        placeholder="Please describe what happened, including how the other driver caused the accident..."
                        className="mt-1 rounded-xl min-h-[120px]"
                        required
                        data-testid="accident-description-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="accident_location" className="text-slate-700 font-medium">
                        Location of Accident
                      </Label>
                      <div className="relative mt-1">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                          id="accident_location"
                          name="accident_location"
                          value={formData.accident_location}
                          onChange={handleInputChange}
                          placeholder="City, State or intersection"
                          className="pl-10 h-12 rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="police_report_available"
                          name="police_report_available"
                          checked={formData.police_report_available}
                          onChange={handleInputChange}
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-[#3B5BDB] focus:ring-[#3B5BDB]"
                        />
                        <div>
                          <Label htmlFor="police_report_available" className="text-slate-700 font-medium cursor-pointer">
                            Police report was filed
                          </Label>
                          <p className="text-sm text-slate-500">A police report strengthens your claim</p>
                        </div>
                      </div>
                      
                      {formData.police_report_available && (
                        <div className="mt-4">
                          <Label htmlFor="police_report_number" className="text-slate-700 font-medium">
                            Police Report Number
                          </Label>
                          <Input
                            id="police_report_number"
                            name="police_report_number"
                            value={formData.police_report_number}
                            onChange={handleInputChange}
                            placeholder="Enter report number if available"
                            className="mt-1 h-12 rounded-xl"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: At-Fault Party */}
              {step === 3 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#E8EEFF] rounded-xl flex items-center justify-center">
                      <User className="h-5 w-5 text-[#3B5BDB]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">At-Fault Party Information</h2>
                      <p className="text-slate-600 text-sm">Information about the other driver (if known)</p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-700">
                        This information helps us process your claim faster. Fill in what you know - you can provide additional details later.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <Label htmlFor="at_fault_name" className="text-slate-700 font-medium">
                        At-Fault Driver's Name
                      </Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                          id="at_fault_name"
                          name="at_fault_name"
                          value={formData.at_fault_name}
                          onChange={handleInputChange}
                          placeholder="John Smith"
                          className="pl-10 h-12 rounded-xl"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="at_fault_insurance" className="text-slate-700 font-medium">
                        Their Insurance Company
                      </Label>
                      <div className="relative mt-1">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                          id="at_fault_insurance"
                          name="at_fault_insurance"
                          value={formData.at_fault_insurance}
                          onChange={handleInputChange}
                          placeholder="State Farm, Geico, Progressive, etc."
                          className="pl-10 h-12 rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="at_fault_policy_number" className="text-slate-700 font-medium">
                          Their Policy Number
                        </Label>
                        <Input
                          id="at_fault_policy_number"
                          name="at_fault_policy_number"
                          value={formData.at_fault_policy_number}
                          onChange={handleInputChange}
                          placeholder="Policy number"
                          className="mt-1 h-12 rounded-xl"
                        />
                      </div>
                      <div>
                        <Label htmlFor="at_fault_claim_number" className="text-slate-700 font-medium">
                          Their Claim Number
                        </Label>
                        <Input
                          id="at_fault_claim_number"
                          name="at_fault_claim_number"
                          value={formData.at_fault_claim_number}
                          onChange={handleInputChange}
                          placeholder="If claim already filed"
                          className="mt-1 h-12 rounded-xl"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Repair Information */}
              {step === 4 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#E8EEFF] rounded-xl flex items-center justify-center">
                      <Wrench className="h-5 w-5 text-[#3B5BDB]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Repair Information</h2>
                      <p className="text-slate-600 text-sm">Tell us about your vehicle's repairs</p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <Label className="text-slate-700 font-medium">Repair Status</Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                        {[
                          { value: 'not_started', label: 'Not Started' },
                          { value: 'in_progress', label: 'In Progress' },
                          { value: 'completed', label: 'Completed' }
                        ].map(option => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, repair_status: option.value }))}
                            className={`p-4 rounded-xl border-2 text-center transition-all ${
                              formData.repair_status === option.value
                                ? 'border-[#3B5BDB] bg-[#E8EEFF] text-[#3B5BDB]'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="repair_shop_name" className="text-slate-700 font-medium">
                        Repair Shop Name
                      </Label>
                      <Input
                        id="repair_shop_name"
                        name="repair_shop_name"
                        value={formData.repair_shop_name}
                        onChange={handleInputChange}
                        placeholder="Name of body shop"
                        className="mt-1 h-12 rounded-xl"
                      />
                    </div>

                    <div>
                      <Label htmlFor="repair_shop_address" className="text-slate-700 font-medium">
                        Repair Shop Address
                      </Label>
                      <Input
                        id="repair_shop_address"
                        name="repair_shop_address"
                        value={formData.repair_shop_address}
                        onChange={handleInputChange}
                        placeholder="Address of repair shop"
                        className="mt-1 h-12 rounded-xl"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="estimated_repair_cost" className="text-slate-700 font-medium">
                          Estimated Repair Cost
                        </Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                          <Input
                            id="estimated_repair_cost"
                            name="estimated_repair_cost"
                            type="number"
                            value={formData.estimated_repair_cost}
                            onChange={handleInputChange}
                            placeholder="0.00"
                            className="pl-8 h-12 rounded-xl"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="actual_repair_cost" className="text-slate-700 font-medium">
                          Actual Repair Cost (if known)
                        </Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                          <Input
                            id="actual_repair_cost"
                            name="actual_repair_cost"
                            type="number"
                            value={formData.actual_repair_cost}
                            onChange={handleInputChange}
                            placeholder="0.00"
                            className="pl-8 h-12 rounded-xl"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Documents */}
              {step === 5 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#E8EEFF] rounded-xl flex items-center justify-center">
                      <Paperclip className="h-5 w-5 text-[#3B5BDB]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Upload Documents</h2>
                      <p className="text-slate-600 text-sm">Add supporting documents to strengthen your claim (optional)</p>
                    </div>
                  </div>

                  {/* Document Types Guide */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-2">Helpful documents to include:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li><strong>Repair Estimate</strong> - From the body shop showing damage assessment</li>
                          <li><strong>Accident Photos</strong> - Pictures of the damage to your vehicle</li>
                          <li><strong>Police Report</strong> - If available from the accident</li>
                          <li><strong>Insurance Correspondence</strong> - Letters from the at-fault party's insurer</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Note about uploading later */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> You can skip this step and upload documents later from your claim details page.
                    </p>
                  </div>

                  {/* Uploaded Documents Preview (shows documents already uploaded in this session) */}
                  {uploadedDocuments.length > 0 && (
                    <div className="mb-6">
                      <Label className="text-slate-700 font-medium mb-3 block">
                        Documents Ready ({uploadedDocuments.length}/10)
                      </Label>
                      <div className="space-y-2">
                        {uploadedDocuments.map((doc, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-3 p-3 rounded-lg border border-green-200 bg-green-50"
                          >
                            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{doc.filename}</p>
                              <p className="text-xs text-slate-500">{doc.document_type_label}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* File Upload Area */}
                  <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-slate-400 transition-colors cursor-pointer"
                    onClick={() => document.getElementById('claim-file-input')?.click()}
                  >
                    <Upload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-700 font-medium mb-2">Click to select files</p>
                    <p className="text-sm text-slate-500">JPG, PNG, PDF, DOC, DOCX up to 25 MB each</p>
                    <p className="text-xs text-slate-400 mt-2">Maximum 10 files per claim</p>
                    <input
                      id="claim-file-input"
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.heic,.heif,.webp,.pdf,.doc,.docx"
                      className="hidden"
                      onChange={async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length === 0) return;
                        
                        const token = getToken();
                        if (!token) {
                          setError('Session expired. Please log in again.');
                          return;
                        }

                        // We'll store files locally for now, upload after claim submission
                        // Because we need claim_number to upload documents
                        for (const file of files) {
                          if (uploadedDocuments.length >= 10) {
                            setError('Maximum 10 files allowed per claim');
                            break;
                          }
                          
                          // Validate file
                          const ext = '.' + file.name.toLowerCase().split('.').pop();
                          const allowedExts = ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.webp', '.pdf', '.doc', '.docx'];
                          if (!allowedExts.includes(ext)) {
                            setError(`File type ${ext} not allowed`);
                            continue;
                          }
                          if (file.size > 25 * 1024 * 1024) {
                            setError(`File ${file.name} exceeds 25 MB limit`);
                            continue;
                          }

                          // Store file reference for upload after claim creation
                          setUploadedDocuments(prev => [...prev, {
                            file,
                            filename: file.name,
                            document_type: 'other',
                            document_type_label: 'Other Document',
                            size: file.size
                          }]);
                        }
                        
                        // Reset input
                        e.target.value = '';
                      }}
                    />
                  </div>

                  {/* Remove uploaded file */}
                  {uploadedDocuments.length > 0 && (
                    <div className="mt-4 text-center">
                      <button
                        type="button"
                        onClick={() => setUploadedDocuments([])}
                        className="text-sm text-red-600 hover:text-red-800 underline"
                      >
                        Clear all files
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 6: Review */}
              {step === 6 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#E8EEFF] rounded-xl flex items-center justify-center">
                      <FileText className="h-5 w-5 text-[#3B5BDB]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Review Your Claim</h2>
                      <p className="text-slate-600 text-sm">Please verify all information is correct</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Policy Section */}
                    <div className="bg-slate-50 rounded-xl p-5">
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Shield className="h-4 w-4 text-[#3B5BDB]" />
                        Policy Information
                      </h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Policy Number</p>
                          <p className="font-mono font-medium">{selectedPolicy?.policy_number}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Coverage Amount</p>
                          <p className="font-medium">{formatCurrency(selectedPolicy?.coverage_amount)}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-slate-500">Vehicle</p>
                          <p className="font-medium">
                            {selectedPolicy?.vehicle?.year} {selectedPolicy?.vehicle?.make} {selectedPolicy?.vehicle?.model}{selectedPolicy?.vehicle?.trim ? ` ${selectedPolicy.vehicle.trim}` : ''}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Accident Section */}
                    <div className="bg-slate-50 rounded-xl p-5">
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-[#3B5BDB]" />
                        Accident Details
                      </h3>
                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="text-slate-500">Date of Accident</p>
                          <p className="font-medium">{formatDate(formData.accident_date)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Description</p>
                          <p className="font-medium">{formData.accident_description}</p>
                        </div>
                        {formData.accident_location && (
                          <div>
                            <p className="text-slate-500">Location</p>
                            <p className="font-medium">{formData.accident_location}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-slate-500">Police Report</p>
                          <p className="font-medium">
                            {formData.police_report_available 
                              ? `Yes${formData.police_report_number ? ` - #${formData.police_report_number}` : ''}`
                              : 'No'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* At-Fault Party Section */}
                    {(formData.at_fault_name || formData.at_fault_insurance) && (
                      <div className="bg-slate-50 rounded-xl p-5">
                        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <User className="h-4 w-4 text-[#3B5BDB]" />
                          At-Fault Party
                        </h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {formData.at_fault_name && (
                            <div>
                              <p className="text-slate-500">Name</p>
                              <p className="font-medium">{formData.at_fault_name}</p>
                            </div>
                          )}
                          {formData.at_fault_insurance && (
                            <div>
                              <p className="text-slate-500">Insurance Company</p>
                              <p className="font-medium">{formData.at_fault_insurance}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Repair Section */}
                    <div className="bg-slate-50 rounded-xl p-5">
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Wrench className="h-4 w-4 text-[#3B5BDB]" />
                        Repair Status
                      </h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Status</p>
                          <p className="font-medium capitalize">{formData.repair_status.replace('_', ' ')}</p>
                        </div>
                        {formData.repair_shop_name && (
                          <div>
                            <p className="text-slate-500">Repair Shop</p>
                            <p className="font-medium">{formData.repair_shop_name}</p>
                          </div>
                        )}
                        {formData.estimated_repair_cost && (
                          <div>
                            <p className="text-slate-500">Estimated Cost</p>
                            <p className="font-medium">{formatCurrency(formData.estimated_repair_cost)}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Documents Section */}
                    <div className="bg-slate-50 rounded-xl p-5">
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <Paperclip className="h-4 w-4 text-[#3B5BDB]" />
                        Supporting Documents
                      </h3>
                      {uploadedDocuments.length > 0 ? (
                        <div className="space-y-2">
                          {uploadedDocuments.map((doc, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="font-medium">{doc.filename}</span>
                              <span className="text-slate-500">({doc.document_type_label})</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500">No documents attached - you can add them later</p>
                      )}
                    </div>

                    {/* Disclaimer */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                      <p className="text-sm text-amber-800">
                        By submitting this claim, I certify that all information provided is accurate to the best of my knowledge. 
                        I understand that providing false information may result in claim denial.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 7: Success */}
              {step === 7 && submitSuccess && (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-green-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Claim Submitted Successfully!</h2>
                  <p className="text-slate-600 mb-6">
                    Your claim has been received and is being reviewed by our team.
                  </p>
                  
                  <div className="bg-slate-50 rounded-xl p-5 mb-8 max-w-md mx-auto">
                    <p className="text-sm text-slate-500 mb-1">Your Claim Number</p>
                    <p className="text-xl font-mono font-bold text-[#3B5BDB]">{newClaimNumber}</p>
                  </div>
                  
                  <p className="text-sm text-slate-600 mb-8">
                    A confirmation email has been sent to your email address. You can track your claim status anytime from your dashboard.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                    <Button
                      onClick={() => navigate(`/claims/${newClaimNumber}`)}
                      className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl"
                    >
                      View Claim Details
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/claims')}
                      className="rounded-xl"
                    >
                      Back to Claims
                    </Button>
                  </div>

                  {/* Complaint Guidance Notice */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 max-w-lg mx-auto text-left" data-testid="claim-complaint-notice">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-slate-700 mb-1">Questions or Concerns?</p>
                        <p className="text-slate-600 mb-2">
                          If you have questions about your claim decision or our process, you can contact us or file a complaint at any time. 
                          Your policy documents include your state-specific complaint notice.
                        </p>
                        <button
                          onClick={() => navigate('/help?category=complaints')}
                          className="text-[#3B5BDB] hover:underline font-medium"
                        >
                          Learn about your complaint rights
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              {step <= 6 && (
                <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
                  {step > 1 ? (
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      className="rounded-xl"
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Back
                    </Button>
                  ) : (
                    <div />
                  )}
                  
                  {step < 6 ? (
                    <Button
                      onClick={handleNext}
                      disabled={!validateStep(step)}
                      className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl"
                    >
                      {step === 5 ? (uploadedDocuments.length > 0 ? 'Continue' : 'Skip & Continue') : 'Continue'}
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="bg-[#3B5BDB] hover:bg-[#2F4BC7] text-white rounded-xl"
                      data-testid="submit-claim-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Claim
                          <CheckCircle className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}

            </CardContent>
          </Card>

        </div>
      </div>
    </PortalLayout>
  );
}
